import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { KeyboardEvent, ChangeEvent } from 'react';
import { Icon } from '../../ui/Icon/Icon';
import './DisplayTimePicker.css';

const T2 = 'var(--t2, #475569)';
const T3 = 'var(--t3, #9CA3B8)';

function parseTime24(value: string): { hour: number; minute: number } {
  const raw = (value ?? '').trim();
  if (!raw || !raw.includes(':')) return { hour: 9, minute: 0 };
  const [h, m] = raw.split(':').map(Number);
  return { hour: Number.isFinite(h) ? h : 9, minute: Number.isFinite(m) ? m : 0 };
}

function to24(hour12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

function toTimeString(hour24: number, minute: number): string {
  return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

function formatTime12h(value: string): string {
  const { hour, minute } = parseTime24(value);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${minute.toString().padStart(2, '0')} ${suffix}`;
}

function SpinnerField({ value, onChange, min, max }: { value: number; onChange: (v: number) => void; min: number; max: number }) {
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isFocusedRef = useRef(false);
  const fmt = (n: number) => n.toString().padStart(2, '0');
  const [display, setDisplay] = useState(fmt(value));

  useEffect(() => {
    if (!isFocusedRef.current) setDisplay(fmt(value));
  }, [value]);

  const clamp = (v: number) => (v > max ? min : v < min ? max : v);
  const step = (dir: 1 | -1) => onChange(clamp(value + dir));

  const commitRaw = (raw: string) => {
    const num = parseInt(raw, 10);
    if (Number.isNaN(num)) { setDisplay(fmt(value)); return; }
    const clamped = Math.min(max, Math.max(min, num));
    onChange(clamped);
    setDisplay(fmt(clamped));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDisplay(raw);
    clearTimeout(commitTimeoutRef.current);
    if (raw.length === 0) return;
    if (raw.length === 2) { commitRaw(raw); return; }
    const num = parseInt(raw, 10);
    if (num * 10 > max) { commitRaw(raw); return; }
    commitTimeoutRef.current = setTimeout(() => commitRaw(raw), 800);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); clearTimeout(commitTimeoutRef.current); step(1); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); clearTimeout(commitTimeoutRef.current); step(-1); return; }
    if (e.key === 'Enter') { e.preventDefault(); clearTimeout(commitTimeoutRef.current); commitRaw(e.currentTarget.value.replace(/\D/g, '')); }
  };

  return (
    <div className="display-time-spinner-group">
      <button type="button" onClick={() => step(1)} className="display-time-spinner-btn">
        <Icon name="keyboard_arrow_up" size={18} color={T3} />
      </button>
      <input
        type="text"
        inputMode="numeric"
        role="spinbutton"
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        value={display}
        onFocus={(e) => { isFocusedRef.current = true; e.target.select(); }}
        onBlur={() => { isFocusedRef.current = false; clearTimeout(commitTimeoutRef.current); commitRaw(display.replace(/\D/g, '')); }}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className="display-time-spinner-input"
      />
      <button type="button" onClick={() => step(-1)} className="display-time-spinner-btn">
        <Icon name="keyboard_arrow_down" size={18} color={T3} />
      </button>
    </div>
  );
}

interface DisplayTimePickerProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
}

/** Time picker matching Product Catalog's Session-form time picker (spinners + AM/PM), for UI consistency. Anchored popover, dismissed on outside click — not a blocking modal. */
export default function DisplayTimePicker({ value, onChange, label = 'Time' }: DisplayTimePickerProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelBox, setPanelBox] = useState({ top: 0, left: 0, width: 260 });

  const parsed = parseTime24(value);
  const toHour12 = (h: number) => (h === 0 ? 12 : h > 12 ? h - 12 : h);

  const [draftHour12, setDraftHour12] = useState(toHour12(parsed.hour));
  const [draftMinute, setDraftMinute] = useState(parsed.minute);
  const [draftPeriod, setDraftPeriod] = useState<'AM' | 'PM'>(parsed.hour >= 12 ? 'PM' : 'AM');

  const handleOpen = () => {
    const p = parseTime24(value);
    setDraftHour12(toHour12(p.hour));
    setDraftMinute(p.minute);
    setDraftPeriod(p.hour >= 12 ? 'PM' : 'AM');
    setOpen(true);
  };

  const handleApply = () => {
    onChange(toTimeString(to24(draftHour12, draftPeriod), draftMinute));
    setOpen(false);
  };

  const updatePanelPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const w = Math.max(r.width, 260);
    let left = r.left;
    if (left + w > window.innerWidth - margin) left = window.innerWidth - w - margin;
    left = Math.max(margin, left);
    const estH = 260;
    let top = r.bottom + 6;
    if (top + estH > window.innerHeight - margin) top = Math.max(margin, r.top - estH - 6);
    setPanelBox({ top, left, width: w });
  };

  useLayoutEffect(() => { if (open) updatePanelPosition(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (wrapperRef.current?.contains(t)) return;
      if (t.closest('[data-display-time-popover]')) return;
      setOpen(false);
    };
    const id = window.setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { window.clearTimeout(id); document.removeEventListener('mousedown', handler); };
  }, [open]);

  const popover = open && (
    <div
      data-display-time-popover
      role="dialog"
      aria-modal="false"
      className="display-time-popover"
      style={{ top: panelBox.top, left: panelBox.left, width: panelBox.width }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="display-time-popover-header">
        <span className="display-time-popover-title">Select Time</span>
      </div>

      <div className="display-time-popover-body">
        <SpinnerField value={draftHour12} onChange={setDraftHour12} min={1} max={12} />
        <span className="display-time-colon">:</span>
        <SpinnerField value={draftMinute} onChange={setDraftMinute} min={0} max={59} />
        <div className="display-time-period-group">
          {(['AM', 'PM'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setDraftPeriod(p)}
              className={`display-time-period-btn ${draftPeriod === p ? 'is-active' : ''}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="display-time-popover-footer">
        <button type="button" onClick={() => setOpen(false)} className="display-time-cancel-btn">
          Cancel
        </button>
        <button type="button" onClick={handleApply} className="display-time-apply-btn">
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <div className="display-time-picker-wrapper" ref={wrapperRef}>
      <div className="display-time-picker-label">{label}</div>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        className="display-time-picker-trigger"
      >
        <Icon name="schedule" size={16} color={T3} />
        {formatTime12h(value)}
      </button>

      {typeof document !== 'undefined' && open && createPortal(popover, document.body)}
    </div>
  );
}
